import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import QRCodeStyling from "qr-code-styling";
import api from "../../services/api";
import "./EvenementInvitation.css";

interface ThemeData {
  background: string;
  text: string;
  accent: string;
  button: string;
}

interface Evenement {
  _id: string;
  type: string;
  province: string;
  dateISO: string;
  heureISO: string;
  image: string;
  theme: ThemeData;
  rsvp: boolean;
}

interface InvitationResponse {
  success: boolean;
  evenement: Evenement;
  invite: {
    _id: string;
    nom: string;
    uniqueToken: string;
    statut: string;
  };
}

interface ConfirmResponse {
  success: boolean;
  message: string;
  invite: {
    _id: string;
    nom: string;
    uniqueToken: string;
    statut: string;
  };
}

const API_URL = process.env.REACT_APP_API_URL;

const EvenementInvitation: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [evenement, setEvenement] = useState<Evenement | null>(null);
  const [confirmationStatus, setConfirmationStatus] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const qrRef = useRef<HTMLDivElement>(null);
  const qrCode = useRef<QRCodeStyling | null>(null);
  const [qrInitialized, setQrInitialized] = useState(false);

  useEffect(() => {
    const fetchInvitation = async () => {
      try {
        const response = await api.get<InvitationResponse>(`/evenements/invitation/${token}`);
        if (response.data.success) {
          setEvenement(response.data.evenement);
          setConfirmationStatus(response.data.invite.statut);
        }
      } catch (err: any) {
        setError("Invitation introuvable ou expiree");
      } finally {
        setLoading(false);
      }
    };

    fetchInvitation();
  }, [token]);

  useEffect(() => {
    if (evenement) {
      const oldMeta = document.querySelectorAll('meta[property^="og:"]');
      oldMeta.forEach(meta => meta.remove());
      
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:title');
      meta.setAttribute('content', `Invitation evenement - ${evenement.type}`);
      document.head.appendChild(meta);
      
      const meta2 = document.createElement('meta');
      meta2.setAttribute('property', 'og:description');
      meta2.setAttribute('content', `Vous etes invite a notre evenement. Rejoignez-nous pour celebrer ce moment special`);
      document.head.appendChild(meta2);
      
      const meta3 = document.createElement('meta');
      meta3.setAttribute('property', 'og:image');
      meta3.setAttribute('content', `${API_URL}/uploads/evenements/${evenement.image}`);
      document.head.appendChild(meta3);
      
      const meta4 = document.createElement('meta');
      meta4.setAttribute('property', 'og:url');
      meta4.setAttribute('content', window.location.href);
      document.head.appendChild(meta4);
      
      const meta5 = document.createElement('meta');
      meta5.setAttribute('property', 'og:type');
      meta5.setAttribute('content', 'website');
      document.head.appendChild(meta5);
      
      return () => {
        const metas = document.querySelectorAll('meta[property^="og:"]');
        metas.forEach(meta => meta.remove());
      };
    }
  }, [evenement]);

  useEffect(() => {
    if (!loading && !qrInitialized && qrRef.current && token && evenement) {
      qrCode.current = new QRCodeStyling({
        width: 280,
        height: 280,
        data: token,
        margin: 8,
        qrOptions: { typeNumber: 0, mode: "Byte", errorCorrectionLevel: "H" },
        dotsOptions: { color: evenement.theme.button, type: "rounded" },
        backgroundOptions: { color: "#ffffff" },
        cornersSquareOptions: { color: evenement.theme.button, type: "extra-rounded" },
        cornersDotOptions: { color: evenement.theme.button, type: "dot" },
      });
      if (qrRef.current) {
        qrRef.current.innerHTML = "";
        qrCode.current.append(qrRef.current);
      }
      setQrInitialized(true);
    }
  }, [loading, qrInitialized, token, evenement]);

  const handleResponse = async () => {
    if (isSubmitting) return;
    
    let newStatut = "";
    if (confirmationStatus === "confirmed") {
      newStatut = "declined";
    } else if (confirmationStatus === "declined") {
      newStatut = "confirmed";
    } else {
      newStatut = "confirmed";
    }
    
    setIsSubmitting(true);
    try {
      const response = await api.put<ConfirmResponse>(`/evenements/invitation/${token}`, { statut: newStatut });
      if (response.data.success) {
        setConfirmationStatus(newStatut);
      }
    } catch (err: any) {
      alert("Erreur lors de l'operation. Veuillez reessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="invitation-loading">
        <div className="loading-spinner"></div>
        <p>Chargement de votre invitation...</p>
      </div>
    );
  }

  if (error || !evenement) {
    return (
      <div className="invitation-error">
        <svg className="error-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="12"/>
          <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
        <h2>Invitation introuvable</h2>
        <p>{error || "Cette invitation n'existe pas ou a expiree."}</p>
      </div>
    );
  }

  const imageUrl = evenement.image 
    ? `${API_URL}/uploads/evenements/${evenement.image}`
    : "";

  const isConfirmed = confirmationStatus === "confirmed";

  return (
    <div className="invitation-wrapper">
      <div className="hero-section" style={{ backgroundColor: evenement.theme.background }}>
        {imageUrl && (
          <img src={imageUrl} alt="Invitation" className="hero-image" />
        )}
      </div>
      
      <div className="content-section" style={{ backgroundColor: evenement.theme.background }}>
        <div className="content-container">
          <div className="qr-container">
            <div className="qr-frame" style={{ borderColor: evenement.theme.button }}>
              <div className="qr-wrapper" ref={qrRef}></div>
            </div>
          </div>
          
          {evenement.rsvp && (
            <div className="rsvp-container">
              <button
                className={`btn ${isConfirmed ? 'btn-decline' : 'btn-confirm'}`}
                style={isConfirmed ? {
                  backgroundColor: "transparent",
                  borderColor: evenement.theme.accent,
                  color: evenement.theme.text
                } : {
                  backgroundColor: evenement.theme.button,
                  color: evenement.theme.background
                }}
                onClick={handleResponse}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  "Chargement..."
                ) : isConfirmed ? (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                    Decliner
                  </>
                ) : (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 13l4 4L19 7"/>
                    </svg>
                    Confirmer
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EvenementInvitation;